import { InputAdornment } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { useMessage } from "hooks/intl";
import { get } from "lodash";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { DailyGrossValueInLastNDays as DailyGrossValueInLastNDaysType } from "types/segments";
import { formatCurrencyValue, formatNumberValue } from "utilities/numbers";
import { pluralizeValueAdornment } from "utilities/strings";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";

export const initialValuesDailyGrossValueInLastNDays: DailyGrossValueInLastNDaysType =
  {
    days: 0,
    fromAmount: 0,
    toAmount: 0,
    type: "transaction_amount_each_day_in_last_n_days",
  };

export function DailyGrossValueInLastNDays({
  formField,
  values,
  status,
  readOnly,
}: {
  formField: string;
  values: any;
  status: any;
  readOnly?: boolean;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const dataTestId = "segment-conditions-daily-gross-value-in-last-n-days";
  const value = get(values, formField);
  const fromAmountError = get(status, "fromAmount");
  const toAmountError = get(status, "toAmount");
  const daysError = get(status, "days");
  const daysPluralized = message({
    id: pluralizeValueAdornment("day", value.days),
  }).toLowerCase();

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          {message({ id: "from" }).toLocaleLowerCase()}
          <BoxElement
            content={formatCurrencyValue(value?.fromAmount, "", currency)}
            error={fromAmountError}
          />
          {message({ id: "to" }).toLocaleLowerCase()}
          <BoxElement
            content={formatCurrencyValue(value?.toAmount, "", currency)}
            error={toAmountError}
          />
          {message({ id: "in_last" }).toLocaleLowerCase()}
          <BoxElement
            content={formatNumberValue(value?.days, "")}
            error={daysError}
          />
          {daysPluralized}
        </>
      ) : (
        <>
          <NumberFormatInput
            allowNegative={false}
            label={<RequiredFieldLabel text={message({ id: "days" })} />}
            name={`${formField}.days`}
            data-test-id={`${dataTestId}-days-input`}
            className={classes.input}
          />
          <NumberFormatInput
            allowNegative={false}
            allowDecimals
            label={
              <RequiredFieldLabel
                text={message({ id: "dailyGrossValueFrom" })}
              />
            }
            name={`${formField}.fromAmount`}
            data-test-id={`${dataTestId}-equal-or-more-than-input`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{currency}</InputAdornment>
              ),
            }}
            className={classes.input}
          />
          <NumberFormatInput
            allowNegative={false}
            allowDecimals
            label={
              <RequiredFieldLabel text={message({ id: "dailyGrossValueTo" })} />
            }
            name={`${formField}.toAmount`}
            data-test-id={`${dataTestId}-equal-or-less-than-input`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{currency}</InputAdornment>
              ),
            }}
            className={classes.input}
          />
        </>
      )}
    </>
  );
}
